import isPWA from '~/utils/isPWA';


/**
 * Home Screen shortcuts (esp. iOS) capture the address-bar URL, not manifest start_url.
 * 1) Patch manifest so start_url/id/scope are absolute origin root.
 * 2) In browser (not installed PWA), keep the visible URL at `/` when there is no
 *    query/hash — so "Add to Home Screen" stores domain root. Vue Router state stays intact.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const origin = window.location.origin;

  const absolutize = (pathOrUrl: string) => {
    try {
      return new URL(pathOrUrl, origin).href;
    } catch {
      return pathOrUrl;
    }
  };

  const patchManifestLink = async () => {
    const link = document.querySelector('link[rel="manifest"]');

    if (!(link instanceof HTMLLinkElement) || !link.href) {
      return;
    }

    if (link.dataset.pwaRootStartUrl === '1') {
      return;
    }

    try {
      const response = await fetch(link.href);
      const manifest = await response.json() as Record<string, unknown>;

      manifest.id = `${origin}/`;
      manifest.start_url = `${origin}/`;
      manifest.scope = `${origin}/`;

      if (Array.isArray(manifest.icons)) {
        manifest.icons = manifest.icons.map((icon) => {
          if (!icon || typeof icon !== 'object') {
            return icon;
          }

          const item = icon as Record<string, unknown>;

          if (typeof item.src === 'string') {
            return { ...item, src: absolutize(item.src) };
          }

          return item;
        });
      }

      if (Array.isArray(manifest.protocol_handlers)) {
        manifest.protocol_handlers = manifest.protocol_handlers.map((handler) => {
          if (!handler || typeof handler !== 'object') {
            return handler;
          }

          const item = handler as Record<string, unknown>;

          if (typeof item.url === 'string') {
            const path = item.url.replace(/^https?:\/\/[^/]+/i, '');
            const withoutLocale = path.replace(/^\/[a-z]{2}(?=\/|\?|$)/i, '') || '/';
            const normalized = withoutLocale.startsWith('/') ? withoutLocale : `/${withoutLocale}`;

            return { ...item, url: absolutize(normalized) };
          }

          return item;
        });
      }

      if (Array.isArray(manifest.screenshots)) {
        manifest.screenshots = manifest.screenshots.map((shot) => {
          if (!shot || typeof shot !== 'object') {
            return shot;
          }

          const item = shot as Record<string, unknown>;

          if (typeof item.src === 'string') {
            return { ...item, src: absolutize(item.src) };
          }

          return item;
        });
      }

      const blobUrl = URL.createObjectURL(
        new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }),
      );

      link.href = blobUrl;
      link.dataset.pwaRootStartUrl = '1';
    } catch {
      // keep original manifest
    }
  };

  const hidePathInAddressBar = () => {
    // Chrome/Android usually honor manifest start_url; iOS Safari captures the address bar.
    if (isPWA() || !useDevice().isIos) {
      return;
    }

    const { pathname, search, hash } = window.location;

    // Keep token/oauth/query deep-links intact in the address bar
    if (search || hash) {
      return;
    }

    if (pathname === '/') {
      return;
    }

    window.history.replaceState(window.history.state, '', `${origin}/`);
  };

  const run = () => {
    void patchManifestLink();
    hidePathInAddressBar();
  };

  run();

  nuxtApp.hook('page:finish', () => {
    run();
  });

  const router = useRouter();

  router.afterEach(() => {
    nextTick(() => {
      hidePathInAddressBar();
    });
  });
});
